import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Agenda card on the meeting create/edit form — rebuilt 2026-09-01 to be
// IDENTICAL to the meeting-page agenda editor (Phelipe: "match all this…
// it needs to be identical"): many levels (2 spaces per level, ‹ › arrows,
// click a line then Add item to nest under it), bold top-level lines,
// section bands ("== label" lines — purple AI / gray from-meeting, fixed
// titles; blue Custom, renameable), From-last-meeting import with a date
// dropdown, and blank rows pruned when real items arrive. Rows serialize
// newline-joined into the hidden meeting[agenda] the form submits. The
// only page-editor feature missing here is per-line file zones — files
// need a saved meeting to attach to, so they live on the meeting page.
export default class extends Controller {
  static targets = ["checkbox", "section", "list", "hiddenField", "generateLabel", "generateBtn",
    "prevModal", "prevSelect", "stagedField",
                    "modeWrite", "modeFile", "writePane", "filePane",
                    "docZone", "docInput", "docChip", "docName", "docField",
                    "templateIdField", "templateButtonLabel", "updateTemplateItem", "unlinkItem",
                    "templatesModal", "templatesList", "templatesEmpty",
                    "saveTemplateModal", "templateNameInput", "templateError", "saveTemplateBtn"]
  static values = { generateUrl: String, previousUrl: String, meetingId: Number, presignUrl: String,
                    templatesUrl: String, templateName: String, previousTopUrl: String }

  connect() {
    this._csrf = document.querySelector("meta[name='csrf-token']")?.content || ""
    this._stagedFiles = [] // [{lineKey, cache_name, filename}] — filed on save
    const lines = (this.hiddenFieldTarget.value || "").split("\n").filter(s => s.trim())
    lines.forEach(raw => {
      const item = this._parseLine(raw)
      if (!item) return
      this.listTarget.appendChild(item.band ? this._bandEl(item.text) : this._rowEl(item))
    })
    this._onSubmit = () => this._serialize()
    this.element.closest("form")?.addEventListener("submit", this._onSubmit)
    this._setupSortable()
    this._linkedName = this.templateNameValue || ""
  }

  // ── Agenda templates (Larry item 20, 2026-09-01; Phelipe 2026-09-03) ──
  // A template is a snapshot: "Save as new" copies the agenda as it stands,
  // "Use" loads one (undo-able), "Update" overwrites the linked one on purpose.
  _templateHeaders() {
    return { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrf }
  }

  _setLinked(id, name) {
    if (this.hasTemplateIdFieldTarget) this.templateIdFieldTarget.value = id || ""
    if (this.hasTemplateButtonLabelTarget) this.templateButtonLabelTarget.textContent = id ? `Template: ${name}` : "Templates"
    if (this.hasUpdateTemplateItemTarget) this.updateTemplateItemTarget.classList.toggle("hidden", !id)
    if (this.hasUnlinkItemTarget) this.unlinkItemTarget.classList.toggle("hidden", !id)
    this._linkedName = name || ""
  }

  stopModal(event) { event.stopPropagation() }
  closeTemplates() { this.templatesModalTarget.classList.add("hidden") }
  closeSaveTemplate() { this.saveTemplateModalTarget.classList.add("hidden") }

  async openTemplates() {
    this.templatesModalTarget.classList.remove("hidden")
    this.templatesListTarget.classList.remove("hidden")
    this.templatesListTarget.innerHTML = `<div class="px-3 py-3 text-[13px] text-gray-400">Loading…</div>`
    try {
      const resp = await fetch(this.templatesUrlValue, { headers: { "Accept": "application/json" } })
      const list = await resp.json()
      this.templatesListTarget.innerHTML = ""
      this.templatesEmptyTarget.classList.toggle("hidden", list.length > 0)
      this.templatesListTarget.classList.toggle("hidden", list.length === 0)
      list.forEach(t => {
        const row = document.createElement("div")
        row.className = "flex items-center gap-2 px-3 py-2"
        row.dataset.templateId = t.id
        row.innerHTML = `
          <div class="flex-1 min-w-0">
            <div data-name class="text-[13px] font-medium text-gray-800 truncate"></div>
            <div class="text-[13px] text-gray-400">${t.lines} line${t.lines === 1 ? "" : "s"} · updated ${t.updated}</div>
          </div>
          <button type="button" data-use class="px-2.5 py-1 text-[13px] font-medium text-white bg-brand hover:bg-brand-dark rounded-lg cursor-pointer">Use</button>
          ${t.can_edit ? `<button type="button" data-del class="p-1 text-gray-300 hover:text-red-600 cursor-pointer" title="Delete template">✕</button>` : ""}`
        row.querySelector("[data-name]").textContent = t.name
        row.querySelector("[data-use]").addEventListener("click", () => this.useTemplate(t.id, t.name))
        row.querySelector("[data-del]")?.addEventListener("click", () => this.deleteTemplate(t.id, t.name, row))
        this.templatesListTarget.appendChild(row)
      })
    } catch (_) {
      this.templatesListTarget.innerHTML = `<div class="px-3 py-3 text-[13px] text-red-600">Couldn't load templates.</div>`
    }
  }

  async useTemplate(id, name) {
    try {
      const resp = await fetch(`${this.templatesUrlValue}/${id}`, { headers: { "Accept": "application/json" } })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't load that template")
      this._snapshot()
      this._restoreState(data.agenda || "")
      this._setLinked(id, name)
      this.closeTemplates()
      this._toast(`Agenda loaded from "${name}"`)
    } catch (err) {
      this._toast(err.message, "alert")
    }
  }

  openSaveTemplate() {
    if (!this._captureState().trim()) return this._toast("Add something to the agenda first.", "alert")
    this.templateErrorTarget.classList.add("hidden")
    this.templateNameInputTarget.value = ""
    this.saveTemplateModalTarget.classList.remove("hidden")
    setTimeout(() => this.templateNameInputTarget.focus(), 50)
  }

  async saveTemplate() {
    const name = this.templateNameInputTarget.value.trim()
    if (!name) {
      this.templateErrorTarget.textContent = "Give the template a name."
      return this.templateErrorTarget.classList.remove("hidden")
    }
    this.saveTemplateBtnTarget.disabled = true
    try {
      const resp = await fetch(this.templatesUrlValue, { method: "POST", headers: this._templateHeaders(),
                                                        body: JSON.stringify({ name, agenda: this._captureState() }) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't save the template")
      this._setLinked(data.id, data.name)
      this.closeSaveTemplate()
      this._toast(`Saved as template "${data.name}"`)
    } catch (err) {
      this.templateErrorTarget.textContent = err.message
      this.templateErrorTarget.classList.remove("hidden")
    } finally {
      this.saveTemplateBtnTarget.disabled = false
    }
  }

  async updateTemplate() {
    const id = this.hasTemplateIdFieldTarget && this.templateIdFieldTarget.value
    if (!id) return
    try {
      const resp = await fetch(`${this.templatesUrlValue}/${id}`, { method: "PATCH", headers: this._templateHeaders(),
                                                                   body: JSON.stringify({ agenda: this._captureState() }) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't update the template")
      this._toast(`Template "${data.name}" updated`)
    } catch (err) {
      this._toast(err.message, "alert")
    }
  }

  unlinkTemplate() {
    this._setLinked(null, "")
    this._toast("Agenda unlinked from the template")
  }

  async deleteTemplate(id, name, row) {
    // Two clicks to delete — the first arms the button (no browser confirm()).
    const btn = row.querySelector("[data-del]")
    if (btn && !btn.dataset.armed) {
      btn.dataset.armed = "1"; btn.textContent = "Delete?"; btn.className = "px-2 py-0.5 text-[13px] font-medium text-red-700 bg-red-50 rounded cursor-pointer"
      setTimeout(() => { if (btn.isConnected) { delete btn.dataset.armed; btn.textContent = "✕"; btn.className = "p-1 text-gray-300 hover:text-red-600 cursor-pointer" } }, 4000)
      return
    }
    try {
      const resp = await fetch(`${this.templatesUrlValue}/${id}`, { method: "DELETE", headers: this._templateHeaders() })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't delete the template")
      row.remove()
      if (this.hasTemplateIdFieldTarget && String(this.templateIdFieldTarget.value) === String(id)) this._setLinked(null, "")
      if (!this.templatesListTarget.children.length) { this.templatesEmptyTarget.classList.remove("hidden"); this.templatesListTarget.classList.add("hidden") }
      this._toast(`Template "${name}" deleted`)
    } catch (err) {
      this._toast(err.message, "alert")
    }
  }

  disconnect() {
    this.element.closest("form")?.removeEventListener("submit", this._onSubmit)
    this._sortable?.destroy()
    this._sortable = null
  }

  toggle() {
    const on = this.checkboxTarget.checked
    this.sectionTarget.classList.toggle("hidden", !on)
    // Ticking "Include an agenda" opens an EMPTY agenda (Phelipe
    // 2026-09-01). It used to fire the AI draft on its own, which put
    // suggestions in front of people who only wanted to type their own —
    // "Regenerate with AI" is there when they actually want it. One blank
    // row so there is somewhere to start typing.
    if (on && this._rows().length === 0) {
      this.listTarget.appendChild(this._rowEl({ text: "", level: 0 }))
      this.listTarget.querySelector("input")?.focus()
    }
    this._serialize()
  }

  // ── AI draft — lands under its own purple band, like the page. ──────
  async generate() {
    if (this._generating) return
    this._generating = true
    this._setGenerating(true)

    const title = document.getElementById("meeting_title")?.value || ""
    const kind = document.getElementById("meeting_kind")?.value || ""
    const templateId = document.getElementById("meeting_meeting_template_id")?.value || ""
    const meetingId = this.hasMeetingIdValue ? this.meetingIdValue : ""

    try {
      const resp = await fetch(this.generateUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf },
        credentials: "same-origin",
        body: JSON.stringify({ title, kind, template_id: templateId, meeting_id: meetingId })
      })
      const data = await resp.json()
      const items = (data.items || []).map(raw => this._parseLine(raw)).filter(Boolean).filter(i => !i.band)
      if (items.length) {
        this._snapshot()
        this._pruneEmptyRows()
        this.listTarget.appendChild(this._bandEl("✨ AI suggestions — review, tweak, delete freely"))
        items.forEach(item => this.listTarget.appendChild(this._rowEl(item)))
        this._serialize()
      }
    } catch (e) {
      console.error("[meeting-agenda] generate failed:", e)
    } finally {
      this._generating = false
      this._setGenerating(false)
    }
  }

  _setGenerating(on) {
    if (this.hasGenerateLabelTarget) {
      this.generateLabelTarget.textContent = on ? "Drafting agenda..." : "Regenerate with AI"
    }
    if (this.hasGenerateBtnTarget) {
      this.generateBtnTarget.classList.toggle("opacity-60", on)
      this.generateBtnTarget.classList.toggle("pointer-events-none", on)
    }
  }

  // ── From last meeting — same-type picker, newest preselected. ───────
  async openPrevious(event) {
    event?.preventDefault()
    if (!this.hasPreviousUrlValue) return
    const kind = document.getElementById("meeting_kind")?.value || ""
    if (!kind) {
      this._toast("Pick the meeting type first.", "alert")
      return
    }
    try {
      const resp = await fetch(`${this.previousUrlValue}?kind=${encodeURIComponent(kind)}`,
        { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      const data = await resp.json()
      const meetings = (data.meetings || []).filter(m => !this.hasMeetingIdValue || m.id !== this.meetingIdValue)
      if (!meetings.length) {
        this._toast("No earlier meeting of this type has an agenda yet.", "alert")
        return
      }
      this._prevMeetings = meetings
      this.prevSelectTarget.innerHTML = meetings.map((m, i) =>
        `<option value="${m.id}"${i === 0 ? " selected" : ""}>${this._esc(m.label)}${i === 0 ? " — last meeting" : ""}</option>`).join("")
      this.prevModalTarget.classList.remove("hidden")
    } catch {
      this._toast("Couldn't load past meetings.", "alert")
    }
  }

  closePrevious() { this.prevModalTarget.classList.add("hidden") }
  stopPrevious(event) { event.stopPropagation() }

  // "From last meeting" (Phelipe 2026-09-06): not the whole agenda — the
  // AI's five most important things from the picked meeting, for this one.
  async confirmPrevious() {
    const id = Number(this.prevSelectTarget.value)
    const picked = (this._prevMeetings || []).find(m => m.id === id)
    this.closePrevious()
    if (!picked) return
    this._toast("Picking the 5 most important items…")
    try {
      const title = document.getElementById("meeting_title")?.value || ""
      const kind = document.getElementById("meeting_kind")?.value || ""
      const resp = await fetch(this.previousTopUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this._csrf },
        body: JSON.stringify({ meeting_id: picked.id, title, kind })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't read that meeting")
      const items = (data.items || []).filter(t => t && t.trim())
      if (!items.length) return this._toast("That meeting has nothing to carry forward.", "alert")
      this._snapshot()
      this._pruneEmptyRows()
      this.listTarget.appendChild(this._bandEl(`↩ From meeting ${data.label || picked.label} — top ${items.length}`))
      items.forEach(text => this.listTarget.appendChild(this._rowEl({ text, level: 0 })))
      this._serialize()
    } catch (err) {
      this._toast(err.message, "alert")
    }
  }

  // ── Rows ────────────────────────────────────────────────────────────
  addItem(event) {
    event?.preventDefault()
    this._snapshot()
    const active = this._activeRow
    if (active && active.isConnected && active.parentElement === this.listTarget) {
      const level = Math.min(this._rowLevel(active) + 1, 5)
      const row = this._rowEl({ text: "", level })
      if (active.nextSibling) this.listTarget.insertBefore(row, active.nextSibling)
      else this.listTarget.appendChild(row)
      row.querySelector("input").focus()
    } else {
      // Hand-typed items get their own renameable "Custom" section.
      const tailBand = [...this.listTarget.children].reverse().find(n => n.hasAttribute("data-agenda-band"))
      const inCustomTail = tailBand && this._bandIsCustom(tailBand.dataset.bandLabel || "")
      if (!inCustomTail) this.listTarget.appendChild(this._bandEl("Custom"))
      const row = this._rowEl({ text: "", level: 0 })
      this.listTarget.appendChild(row)
      row.querySelector("input").focus()
    }
    this._serialize()
  }

  rowFocused(event) {
    this._snapshot()
    this._activeRow = event.target.closest("[data-agenda-row]")
  }

  removeRow(event) {
    this._snapshot()
    event.preventDefault()
    event.currentTarget.closest("[data-agenda-row]")?.remove()
    this._serialize()
  }

  indentRow(event) {
    this._snapshot()
    const row = event.currentTarget.closest("[data-agenda-row]")
    if (!row) return
    const prev = row.previousElementSibling
    const cap = prev && prev.hasAttribute("data-agenda-row") ? this._rowLevel(prev) + 1 : 0
    this._styleRow(row, Math.min(this._rowLevel(row) + 1, cap, 5))
    this._serialize()
  }

  outdentRow(event) {
    this._snapshot()
    const row = event.currentTarget.closest("[data-agenda-row]")
    if (!row) return
    this._styleRow(row, Math.max(this._rowLevel(row) - 1, 0))
    this._serialize()
  }

  keydown(event) {
    if (event.key !== "Enter") return
    event.preventDefault()
    const row = event.target.closest("[data-agenda-row]")
    const next = this._rowEl({ text: "", level: this._rowLevel(row) })
    if (row?.nextSibling) this.listTarget.insertBefore(next, row.nextSibling)
    else this.listTarget.appendChild(next)
    next.querySelector("input").focus()
  }

  changed() { this._serialize() }

  _rows() { return [...this.listTarget.querySelectorAll("[data-agenda-row]")] }
  _rowsHost() { return this.listTarget }
  _rowLevel(row) { return Number(row?.dataset?.agendaLevel) || 0 }

  // ── Undo / redo (item 23, 2026-09-01): snapshots of the whole editor,
  // taken before each change — text edits snapshot when a field is first
  // focused, structural changes right before they land. ─────────────────
  _snapshot() {
    this._undoStack = this._undoStack || []
    const state = this._captureState()
    if (this._undoStack[this._undoStack.length - 1] === state) return
    this._undoStack.push(state)
    if (this._undoStack.length > 50) this._undoStack.shift()
    this._redoStack = []
    this._syncUndoButtons()
  }

  undo(event) {
    event?.preventDefault()
    if (!this._undoStack?.length) return
    this._redoStack = this._redoStack || []
    this._redoStack.push(this._captureState())
    this._restoreState(this._undoStack.pop())
    this._syncUndoButtons()
  }

  redo(event) {
    event?.preventDefault()
    if (!this._redoStack?.length) return
    this._undoStack = this._undoStack || []
    this._undoStack.push(this._captureState())
    this._restoreState(this._redoStack.pop())
    this._syncUndoButtons()
  }

  _syncUndoButtons() {
    const set = (sel, on) => {
      const btn = this.element.querySelector(sel)
      if (!btn) return
      btn.classList.toggle("opacity-60", !on)
      btn.classList.toggle("cursor-not-allowed", !on)
      btn.classList.toggle("cursor-pointer", on)
    }
    set("[data-agenda-undo]", !!this._undoStack?.length)
    set("[data-agenda-redo]", !!this._redoStack?.length)
  }

  _captureState() {
    const lines = []
    ;[...this.listTarget.children].forEach(node => {
      if (node.hasAttribute("data-agenda-band")) {
        const label = node.dataset.bandLabel?.trim()
        if (label) lines.push(`== ${label}`)
        return
      }
      if (!node.hasAttribute("data-agenda-row")) return
      const text = node.querySelector("input")?.value ?? ""
      lines.push("  ".repeat(this._rowLevel(node)) + text)
    })
    return lines.join("\n")
  }

  _restoreState(state) {
    const lines = state.split("\n")
    this.listTarget.innerHTML = ""
    lines.forEach(raw => {
      const item = this._parseLine(raw)
      if (!item) return
      this.listTarget.appendChild(item.band ? this._bandEl(item.text) : this._rowEl(item))
    })
    this._serialize()
  }


  _pruneEmptyRows() {
    this._rows().forEach(row => {
      if (!row.querySelector("input")?.value?.trim()) row.remove()
    })
  }

  _rowEl({ text, level, child }) {
    level = Number(level ?? (child ? 1 : 0)) || 0
    const row = document.createElement("div")
    row.setAttribute("data-agenda-row", "")
    row.innerHTML = `
      <span data-agenda-drag class="px-0.5 mt-2 text-gray-300 hover:text-gray-500 cursor-grab shrink-0" title="Drag to reorder">
        <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor"><circle cx="2.5" cy="3" r="1.3"/><circle cx="7.5" cy="3" r="1.3"/><circle cx="2.5" cy="8" r="1.3"/><circle cx="7.5" cy="8" r="1.3"/><circle cx="2.5" cy="13" r="1.3"/><circle cx="7.5" cy="13" r="1.3"/></svg>
      </span>
      <button type="button" data-action="click->meeting-agenda#outdentRow" title="Move out a level"
              class="w-4 h-5 mt-[7px] shrink-0 inline-flex items-center justify-center text-gray-300 hover:text-gray-600 cursor-pointer">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>
      </button>
      <button type="button" data-action="click->meeting-agenda#indentRow" data-agenda-indent title="Move in a level (sub-item)"
              class="w-4 h-5 mt-[7px] shrink-0 inline-flex items-center justify-center text-gray-300 hover:text-gray-600 cursor-pointer">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>
      </button>
      <input type="text" value="" maxlength="500" placeholder="Agenda item"
             data-action="keydown->meeting-agenda#keydown focusin->meeting-agenda#rowFocused input->meeting-agenda#changed"
             class="flex-1 min-w-0 px-2.5 py-1.5 text-[13px] border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">
      <div data-agenda-zone data-action="click->meeting-agenda#zoneClick dragover->meeting-agenda#zoneDragOver dragenter->meeting-agenda#zoneDragOver dragleave->meeting-agenda#zoneDragLeave drop->meeting-agenda#zoneDrop paste->meeting-agenda#zonePaste"
           class="flex w-64 shrink-0 items-center px-2 py-1 min-h-[34px] border border-dashed border-amber-200 bg-amber-50 rounded-lg text-[13px] text-amber-400 hover:text-amber-600 hover:border-amber-300 cursor-pointer transition-colors select-none"
           title="Attach to this line">
        <span data-agenda-zone-hint class="w-full text-center">Drop, paste, or click to attach</span>
        <div data-agenda-chips class="hidden flex-wrap items-center gap-1"></div>
      </div>
      <button type="button" data-action="click->meeting-agenda#removeRow"
              class="w-5 h-5 mt-[7px] shrink-0 text-gray-300 hover:text-red-600 cursor-pointer leading-none text-base" title="Remove">×</button>
    `
    row.querySelector("input").value = text
    this._styleRow(row, level)
    return row
  }

  _styleRow(row, level) {
    level = Math.max(0, Math.min(Number(level) || 0, 5))
    row.dataset.agendaLevel = String(level)
    row.className = "flex items-start gap-1.5"
    row.style.marginLeft = level ? `${level * 1.75}rem` : ""
    const indentBtn = row.querySelector("[data-agenda-indent]")
    if (indentBtn) indentBtn.classList.toggle("text-brand", level > 0)
    const input = row.querySelector("input")
    if (input) {
      input.classList.toggle("text-gray-600", level > 0)
      input.classList.toggle("font-semibold", level === 0)
    }
  }

  // Rows belonging to a band: everything after it up to the next band.
  _bandSectionRows(band) {
    const rows = []
    let node = band.nextElementSibling
    while (node && !node.hasAttribute("data-agenda-band")) {
      if (node.hasAttribute("data-agenda-row")) rows.push(node)
      node = node.nextElementSibling
    }
    return rows
  }

  toggleBandCollapse(band) {
    const collapsed = band.dataset.bandCollapsed === "1"
    band.dataset.bandCollapsed = collapsed ? "0" : "1"
    band.querySelector("[data-band-chevron]")?.classList.toggle("-rotate-90", !collapsed)
    this._bandSectionRows(band).forEach(r => { r.style.display = collapsed ? "" : "none" })
  }

  // Deleting a divider takes its whole section (Phelipe 2026-09-01) —
  // styled confirm first when items would go with it.
  askDeleteBand(band) {
    const rows = this._bandSectionRows(band)
    const filled = rows.filter(r => r.querySelector("input")?.value?.trim()).length
    if (filled === 0) {
      this._snapshot()
      rows.forEach(r => r.remove())
      band.remove()
      this._serialize()
      return
    }
    this._pendingBand = band
    const note = this._bandDeleteNoteEl()
    if (note) note.textContent = `"${band.dataset.bandLabel || "This section"}" and its ${filled} item${filled === 1 ? "" : "s"} are deleted together. This can't be undone here.`
    this._bandDeleteModalEl()?.classList.remove("hidden")
  }

  closeBandDelete() { this._bandDeleteModalEl()?.classList.add("hidden") }
  stopBandDelete(event) { event.stopPropagation() }

  confirmBandDelete() {
    this._snapshot()
    const band = this._pendingBand
    this.closeBandDelete()
    if (!band || !band.isConnected) return
    this._bandSectionRows(band).forEach(r => r.remove())
    band.remove()
    this._pendingBand = null
    this._serialize()
  }

  _bandDeleteModalEl() { return this.element.querySelector("[data-band-delete-modal]") }
  _bandDeleteNoteEl() { return this.element.querySelector("[data-band-delete-note]") }

  _refreshBandCounts() {
    const host = this._rowsHost()
    host.querySelectorAll("[data-agenda-band]").forEach(band => {
      const n = this._bandSectionRows(band).filter(r => r.querySelector("input")?.value?.trim()).length
      const el = band.querySelector("[data-band-count]")
      if (el) el.textContent = `${n} item${n === 1 ? "" : "s"}`
      // Rows that arrived while collapsed stay in step.
      if (band.dataset.bandCollapsed === "1") this._bandSectionRows(band).forEach(r => { r.style.display = "none" })
    })
  }

  _bandIsCustom(label) {
    return !(/AI suggestions|From meeting/i.test(label) || label.startsWith("✨") || label.startsWith("↩"))
  }

  _bandEl(label) {
    const ai = /AI/i.test(label) || label.startsWith("✨")
    const custom = this._bandIsCustom(label)
    const band = document.createElement("div")
    band.setAttribute("data-agenda-band", "")
    band.dataset.bandLabel = label
    const tone = ai ? "bg-purple-50 border-purple-200 text-purple-700"
      : custom ? "bg-blue-50 border-blue-200 text-blue-700"
      : "bg-gray-100 border-gray-200 text-gray-600"
    band.className = `flex items-center gap-1.5 px-2.5 py-1 mt-3 rounded-lg border text-[13px] font-medium ${tone}`
    // Collapsible (Phelipe 2026-09-01): the chevron folds the section's
    // rows away; the count shows how many line items live inside.
    const chev = document.createElement("button")
    chev.type = "button"
    chev.title = "Collapse / expand this section"
    chev.className = "shrink-0 w-4 h-4 inline-flex items-center justify-center text-current opacity-60 hover:opacity-100 cursor-pointer transition-transform"
    chev.setAttribute("data-band-chevron", "")
    chev.innerHTML = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>`
    chev.addEventListener("click", () => this.toggleBandCollapse(band))
    band.append(chev)
    // Drag the whole section (Phelipe 2026-09-01): the handle grabs the
    // divider, and _setupSortable carries its rows along with it.
    const grip = document.createElement("span")
    grip.setAttribute("data-agenda-drag", "")
    grip.className = "px-0.5 text-current opacity-40 hover:opacity-80 cursor-grab shrink-0"
    grip.title = "Drag to move this whole section"
    grip.innerHTML = `<svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor"><circle cx="2.5" cy="3" r="1.3"/><circle cx="7.5" cy="3" r="1.3"/><circle cx="2.5" cy="8" r="1.3"/><circle cx="7.5" cy="8" r="1.3"/><circle cx="2.5" cy="13" r="1.3"/><circle cx="7.5" cy="13" r="1.3"/></svg>`
    band.append(grip)
    if (custom) {
      const input = document.createElement("input")
      input.type = "text"
      input.maxLength = 200
      input.value = label
      input.className = "flex-1 min-w-0 bg-transparent border-0 p-0 text-[13px] font-medium text-current focus:outline-none focus:ring-0"
      input.addEventListener("input", () => { band.dataset.bandLabel = input.value; this._serialize() })
      band.append(input)
    } else {
      const span = document.createElement("span")
      span.className = "flex-1 truncate"
      span.textContent = label
      band.append(span)
    }
    const count = document.createElement("span")
    count.setAttribute("data-band-count", "")
    count.className = "shrink-0 text-[11px] font-normal opacity-70 tabular-nums"
    band.append(count)
    const x = document.createElement("button")
    x.type = "button"
    x.className = "text-current opacity-50 hover:opacity-100 cursor-pointer leading-none"
    x.title = "Remove this separator"
    x.textContent = "×"
    x.addEventListener("click", () => this.askDeleteBand(band))
    band.append(x)
    return band
  }

  _parseLine(raw) {
    if (raw.trim().startsWith("== ")) {
      const label = raw.trim().slice(3).trim()
      return label ? { text: label, band: true } : null
    }
    const tabs = (raw.match(/^\t+/) || [""])[0].length
    const spaces = (raw.match(/^ +/) || [""])[0].length
    const level = Math.min(tabs > 0 ? tabs : Math.floor(spaces / 2), 5)
    const text = raw.trim().replace(/^[-•*]\s+/, "")
    return text ? { text, level } : null
  }

  _serialize() {
    if (!this.checkboxTarget.checked) {
      this.hiddenFieldTarget.value = ""
      return
    }
    const lines = []
    ;[...this.listTarget.children].forEach(node => {
      if (node.hasAttribute("data-agenda-band")) {
        const label = node.dataset.bandLabel?.trim()
        if (label) lines.push(`== ${label}`)
        return
      }
      if (!node.hasAttribute("data-agenda-row")) return
      const text = node.querySelector("input")?.value?.trim()
      if (!text) return
      lines.push("  ".repeat(this._rowLevel(node)) + text)
    })
    this.hiddenFieldTarget.value = lines.join("\n")
    this._serializeStaged()
    this._refreshBandCounts()
  }

  // ── Agenda-line attachments on the form (Phelipe 2026-09-01) ────────
  // The meeting has no id yet, so files go to S3 now and the staged list
  // rides the form; the server files them against their line on save.

  zoneClick(e) {
    e.preventDefault()
    const zone = e.currentTarget
    const picker = document.createElement("input")
    picker.type = "file"
    picker.multiple = true
    picker.className = "hidden"
    picker.addEventListener("change", () => {
      this._stageForZone(zone, [...(picker.files || [])])
      picker.remove()
    })
    document.body.appendChild(picker)
    picker.click()
  }

  zoneDragOver(e) {
    e.preventDefault()
    e.currentTarget.classList.add("border-amber-400", "bg-amber-100")
  }

  zoneDragLeave(e) {
    e.currentTarget.classList.remove("border-amber-400", "bg-amber-100")
  }

  zoneDrop(e) {
    e.preventDefault()
    this.zoneDragLeave(e)
    this._stageForZone(e.currentTarget, [...(e.dataTransfer?.files || [])])
  }

  zonePaste(e) {
    const files = [...(e.clipboardData?.files || [])]
    if (files.length === 0) return
    e.preventDefault()
    this._stageForZone(e.currentTarget, files)
  }

  async _stageForZone(zone, files) {
    if (!files.length || !this.hasPresignUrlValue) return

    const row = zone.closest("[data-agenda-row]")
    const named = files.map((file, i) => (
      file.name ? file : new File([file], `pasted-${Date.now()}${files.length > 1 ? `-${i + 1}` : ""}.png`, { type: file.type || "image/png" })
    ))

    for (const file of named) {
      const chip = this._zoneChip(zone, file.name, true)
      try {
        const resp = await fetch(this.presignUrlValue, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf },
          credentials: "same-origin",
          body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
        })
        const data = await resp.json()
        const upload = data.uploads?.[0]
        if (!resp.ok || !upload) throw new Error(data.error || "presign failed")

        const put = await fetch(upload.upload_url, { method: "PUT", body: file })
        if (!put.ok) throw new Error(`S3 ${put.status}`)

        this._stagedFiles.push({ row, cache_name: upload.cache_name, filename: file.name })
        this._zoneChipDone(chip)
        this._serialize()
      } catch (err) {
        console.error("[meeting-agenda] attach failed:", err)
        chip.remove()
        this._toast("That file didn't attach — try again.", "alert")
      }
    }
    this._refreshZone(zone)
  }

  _zoneChip(zone, filename, uploading) {
    const chips = zone.querySelector("[data-agenda-chips]")
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white border border-amber-200 text-[11px] text-amber-700 max-w-[110px]"
    chip.innerHTML = `<span class="truncate">${this._esc(filename)}</span>`
    if (uploading) chip.classList.add("opacity-60")
    chips.appendChild(chip)
    this._refreshZone(zone)
    return chip
  }

  _zoneChipDone(chip) {
    chip.classList.remove("opacity-60")
  }

  _refreshZone(zone) {
    const chips = zone.querySelector("[data-agenda-chips]")
    const hint = zone.querySelector("[data-agenda-zone-hint]")
    const has = chips.children.length > 0
    chips.classList.toggle("hidden", !has)
    chips.classList.toggle("flex", has)
    if (hint) hint.classList.toggle("hidden", has)
  }

  // Staged files are keyed by the row's CURRENT text, so renaming a line
  // before saving still lands its files on the right one.
  // ── Agenda as an attached file (Phelipe 2026-09-01) ─────────────────
  // Some agendas already exist as a PDF or Word doc; this lets them ride as
  // the agenda itself instead of being retyped. It reuses the same staging
  // the per-line attachments use, under a reserved line key, so the server
  // needs no special case.
  static AGENDA_DOC_KEY = "__agenda_document__"

  modeChanged() {
    const useFile = this.hasModeFileTarget && this.modeFileTarget.checked
    if (this.hasWritePaneTarget) this.writePaneTarget.classList.toggle("hidden", useFile)
    if (this.hasFilePaneTarget) this.filePaneTarget.classList.toggle("hidden", !useFile)
  }

  browseDoc() { this.docInputTarget?.click() }

  dragOverDoc(e) {
    e.preventDefault()
    e.currentTarget.classList.add("border-brand", "bg-brand/5")
  }

  dragLeaveDoc(e) {
    e.currentTarget.classList.remove("border-brand", "bg-brand/5")
  }

  dropDoc(e) {
    e.preventDefault()
    e.currentTarget.classList.remove("border-brand", "bg-brand/5")
    const file = e.dataTransfer?.files?.[0]
    if (file) this._uploadAgendaDoc(file)
  }

  docChosen(e) {
    const file = e.currentTarget.files?.[0]
    if (file) this._uploadAgendaDoc(file)
    e.currentTarget.value = ""
  }

  removeDoc() {
    this._agendaDoc = null
    this._stagedFiles = this._stagedFiles.filter(f => !f.agendaDoc)
    if (this.hasDocFieldTarget) this.docFieldTarget.value = ""
    if (this.hasDocChipTarget) this.docChipTarget.classList.add("hidden")
    if (this.hasDocZoneTarget) this.docZoneTarget.classList.remove("hidden")
    this._serialize()
  }

  async _uploadAgendaDoc(file) {
    if (!this.hasPresignUrlValue) return

    // One agenda document, not a pile — a new pick replaces the old.
    this._stagedFiles = this._stagedFiles.filter(f => !f.agendaDoc)
    if (this.hasDocNameTarget) this.docNameTarget.textContent = `${file.name} …`
    if (this.hasDocChipTarget) this.docChipTarget.classList.remove("hidden")
    if (this.hasDocZoneTarget) this.docZoneTarget.classList.add("hidden")

    try {
      const resp = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf },
        credentials: "same-origin",
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const data = await resp.json()
      const upload = data.uploads?.[0]
      if (!resp.ok || !upload) throw new Error(data.error || "presign failed")

      const put = await fetch(upload.upload_url, { method: "PUT", body: file })
      if (!put.ok) throw new Error(`S3 ${put.status}`)

      this._stagedFiles.push({ agendaDoc: true, cache_name: upload.cache_name, filename: file.name })
      if (this.hasDocNameTarget) this.docNameTarget.textContent = file.name
      this._serialize()
    } catch (err) {
      console.error("[meeting-agenda] agenda file failed:", err)
      this.removeDoc()
      this._toast("That file didn't attach — try again.", "alert")
    }
  }

  _serializeStaged() {
    if (!this.hasStagedFieldTarget) return
    const payload = this._stagedFiles
      // The agenda document has no row — it files under the reserved key.
      .filter(f => f.agendaDoc || f.row?.isConnected)
      .map(f => ({
        line_key: f.agendaDoc ? this.constructor.AGENDA_DOC_KEY : (f.row.querySelector("input")?.value || "").trim(),
        cache_name: f.cache_name,
        filename: f.filename
      }))
      .filter(f => f.line_key)
    this.stagedFieldTarget.value = payload.length ? JSON.stringify(payload) : ""
  }

  _setupSortable() {
    if (this._sortable) return
    this._sortable = Sortable.create(this.listTarget, {
      handle: "[data-agenda-drag]",
      animation: 120,
      // Larry item 22 (2026-09-01): a row could not be dropped LAST — past the
      // final row's midpoint there was nothing to swap with. Inverted swap
      // zones plus the list's bottom padding give the end a real drop target.
      invertSwap: true,
      swapThreshold: 0.65,
      emptyInsertThreshold: 12,
      onStart: (evt) => {
        this._snapshot()
        // Dragging a divider moves its whole section (Phelipe 2026-09-01).
        // Its rows are lifted out for the duration so Sortable only has the
        // divider to place; they are put back under it on drop.
        this._draggedBand = evt.item?.hasAttribute("data-agenda-band") ? evt.item : null
        this._bandMembers = this._draggedBand ? this._sectionRows(this._draggedBand) : []
        this._bandMembers.forEach(node => node.remove())
      },
      onEnd: () => {
        if (this._draggedBand) {
          this._reattachBand(this._draggedBand, this._bandMembers)
          this._draggedBand = null
          this._bandMembers = []
        }
        this._serialize()
      }
    })
  }

  // The rows belonging to a divider: everything after it up to the next one.
  _sectionRows(band) {
    const rows = []
    let node = band.nextElementSibling
    while (node && !node.hasAttribute("data-agenda-band")) {
      rows.push(node)
      node = node.nextElementSibling
    }
    return rows
  }

  // Put a moved section's rows back under its divider. A section dropped
  // INSIDE another one would otherwise adopt that section's remaining rows,
  // so it snaps past them and lands whole, right after that section.
  _reattachBand(band, members) {
    let anchor = band
    while (anchor.nextElementSibling && !anchor.nextElementSibling.hasAttribute("data-agenda-band")) {
      anchor = anchor.nextElementSibling
    }
    if (anchor !== band) band.parentNode.insertBefore(band, anchor.nextElementSibling)

    let after = band
    members.forEach(node => {
      after.parentNode.insertBefore(node, after.nextElementSibling)
      after = node
    })
  }

  _toast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.textContent = message
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    document.body.prepend(div)
  }

  _esc(s) {
    return (s || "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }
}
